import React, { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Dropdown, Form } from 'react-bootstrap';
import { FaChevronDown, FaXmark } from 'react-icons/fa6';

const COLLAPSE_ANIMATION_MS = 350;
const DROPDOWN_Z_INDEX = 'var(--z-dropdown)';

function DropdownGroupSection({ groupKey, children }) {
  const [isOpen, setIsOpen] = useState(true);
  const isAnimatingRef = useRef(false);
  const animationGuardTimeoutRef = useRef(null);

  useEffect(() => {
    return () => window.clearTimeout(animationGuardTimeoutRef.current);
  }, []);

  const releaseAnimationGuard = () => {
    isAnimatingRef.current = false;
    window.clearTimeout(animationGuardTimeoutRef.current);
  };

  const onToggleSection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    setIsOpen((prev) => !prev);
    window.clearTimeout(animationGuardTimeoutRef.current);
    animationGuardTimeoutRef.current = window.setTimeout(
      releaseAnimationGuard,
      COLLAPSE_ANIMATION_MS,
    );
  };

  const sectionId = `graph-dropdown-section-${`${groupKey}`.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <div className="mb-1">
      <Button
        type="button"
        variant="primary"
        className="fusion-theme-dropdown-toggle text-center fw-bold px-2 py-1 w-100 d-flex align-items-center justify-content-center gap-2 rounded-0"
        onClick={onToggleSection}
        aria-expanded={isOpen}
        aria-controls={sectionId}
      >
        <span className="text-truncate">{groupKey}</span>
        <FaChevronDown
          size={12}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: `transform ${COLLAPSE_ANIMATION_MS}ms ease`,
          }}
        />
      </Button>

      <Collapse in={isOpen} onEntered={releaseAnimationGuard} onExited={releaseAnimationGuard}>
        <div id={sectionId}>{children}</div>
      </Collapse>
    </div>
  );
}

/**
 * Dropdown select component. Primarily used in Connected Devices Device Graph feature for Device Selection due to unique behavior.
 *
 * Supports single and multiple selection modes, optional checkboxes,
 * grouped options, and disabled options.
 *
 * @param {Object} props
 * @param {Array} [props.options=[]] - Array of options to display. Each option can be a primitive or an object with `label` and `value` keys.
 * @param {string|number|Array} [props.selectedOption] - Currently selected option(s). For multiple selection, this should be an array.
 * @param {(value: string|number|Array) => void} props.setSelected - Setter function to update the selected option(s).
 * @param {boolean} [props.multiple=false] - Whether multiple selection is allowed.
 * @param {boolean} [props.haveCheckboxes=false] - Whether to show checkboxes next to options.
 * @param {(event: React.SyntheticEvent, value: string|number, index: number) => void} [props.onChange] - Optional callback when an option is selected.
 * @param {React.CSSProperties} [props.triggerStyle] - Optional styles for the dropdown trigger button.
 * @param {React.CSSProperties} [props.contentStyle] - Optional styles for the dropdown menu content.
 * @param {(option: any, index: number) => boolean} [props.isOptionDisabled] - Function to determine if an option should be disabled.
 * @param {() => void} [props.onClearAll] - Optional callback to clear all selections in multiple mode.
 * @returns {JSX.Element}
 */
export default function DropdownSelect({
  options = [],
  selectedOption,
  setSelected,
  multiple = false,
  haveCheckboxes = false,
  onChange,
  triggerClassName = 'w-100 d-flex align-items-center rounded-2 no-caret',
  triggerStyle,
  contentStyle,
  isOptionDisabled = () => false,
  onClearAll,
  placeholder = 'Select Options',
  menuOnly = false,
  emptyMessage = 'No options found.',
}) {
  const isOptionSelected = (option) => {
    const value = optionValue(option);
    if (multiple) return Array.isArray(selectedOption) && selectedOption.includes(value);
    return selectedOption === value;
  };

  const optionValue = (option) =>
    option && typeof option === 'object' ? (option.value ?? option.label ?? option) : option;
  const optionLabel = (option) =>
    option && typeof option === 'object' ? (option.label ?? option.value) : option;

  const handleClearAll = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!multiple || !Array.isArray(selectedOption) || selectedOption.length === 0) return;
    if (typeof onClearAll === 'function') onClearAll();
    else setSelected([]);
  };

  const handleSelect = (index, event, option) => {
    const value = optionValue(option);
    if (typeof onChange === 'function') onChange(event, value, index);
    if (!multiple) return setSelected(value);
    if (!Array.isArray(selectedOption)) return setSelected([value]);
    return selectedOption.includes(value)
      ? setSelected(selectedOption.filter((existing) => existing !== value))
      : setSelected([...selectedOption, value]);
  };

  const dropdownLabel = () => {
    if (!multiple) {
      const opt = options.find((o) => optionValue(o) === selectedOption);
      return optionLabel(opt) || placeholder;
    }
    if (!Array.isArray(selectedOption) || selectedOption.length === 0) return placeholder;
    return selectedOption
      .map((selectedValue) => {
        const opt = options.find((o) => optionValue(o) === selectedValue);
        return optionLabel(opt) || '';
      })
      .join(', ');
  };

  const firstOption = options && options.length ? options[0] : null;
  const optionsAreGrouped =
    firstOption && typeof firstOption === 'object' && 'group' in firstOption;

  const renderSimple = () =>
    options.map((option, index) => {
      const disabled = !!isOptionDisabled(option, index);
      const value = optionValue(option) ?? index;
      return (
        <Dropdown.Item
          key={`${value}-${index}`}
          className="rounded-2 fusion-theme-dropdown-item"
          active={optionValue(option) === selectedOption}
          disabled={disabled}
          style={{ opacity: disabled ? 0.6 : 1 }}
          onClick={(e) => {
            if (disabled) return;
            handleSelect(index, e, option);
          }}
        >
          {haveCheckboxes || multiple ? (
            <Form.Check
              type="checkbox"
              label={optionLabel(option)}
              disabled={disabled}
              checked={isOptionSelected(option)}
              onChange={() => {}}
              readOnly
              style={{ opacity: disabled ? 0.6 : 1, pointerEvents: 'none' }}
            />
          ) : (
            optionLabel(option)
          )}
        </Dropdown.Item>
      );
    });

  const renderGrouped = () => {
    const grouped = new Map();
    options.forEach((option, index) => {
      const groupKey = option.group ?? 'Ungrouped';
      if (!grouped.has(groupKey)) grouped.set(groupKey, []);
      grouped.get(groupKey).push({ option, index });
    });
    return Array.from(grouped.entries()).map(([groupKey, items]) => (
      <DropdownGroupSection key={`group-${groupKey}`} groupKey={groupKey}>
        {items.map(({ option, index }) => {
          const disabled = !!isOptionDisabled(option, index);
          const value = optionValue(option) ?? index;
          return (
            <Dropdown.Item
              key={`${value}-${index}-${groupKey}`}
              className="rounded-2 fusion-theme-dropdown-item"
              active={optionValue(option) === selectedOption}
              disabled={disabled}
              style={{ opacity: disabled ? 0.6 : 1 }}
              onClick={(e) => {
                if (disabled) return;
                handleSelect(index, e, option);
              }}
            >
              {haveCheckboxes || multiple ? (
                <Form.Check
                  type="checkbox"
                  label={optionLabel(option)}
                  disabled={disabled}
                  checked={isOptionSelected(option)}
                  onChange={() => {}}
                  readOnly
                  style={{ opacity: disabled ? 0.6 : 1, pointerEvents: 'none' }}
                />
              ) : (
                optionLabel(option)
              )}
            </Dropdown.Item>
          );
        })}
      </DropdownGroupSection>
    ));
  };

  if (menuOnly) {
    return (
      <div
        className="dropdown-menu show position-static d-block p-1 w-100 fusion-theme-dropdown-menu"
        style={{ zIndex: DROPDOWN_Z_INDEX, ...contentStyle }}
      >
        {options.length === 0 ? (
          <div className="dropdown-item rounded-2 fusion-theme-dropdown-item px-3 py-2">
            {emptyMessage}
          </div>
        ) : optionsAreGrouped ? (
          renderGrouped()
        ) : (
          renderSimple()
        )}
      </div>
    );
  }

  const menu = (
    <Dropdown.Menu
      className="p-1 w-100 fusion-theme-dropdown-menu"
      style={{ zIndex: DROPDOWN_Z_INDEX, ...contentStyle }}
      // `popperConfig` is a Dropdown.Menu prop. It used to sit on the root
      // <Dropdown>, which forwards unknown props to its <div> — hence React's
      // "does not recognize the `popperConfig` prop on a DOM element" warning.
      // Same intent as before: fixed strategy so the menu escapes overflow
      // containers.
      popperConfig={{ strategy: 'fixed' }}
    >
      {options.length === 0 ? (
        <div className="dropdown-item rounded-2 fusion-theme-dropdown-item px-3 py-2">
          {emptyMessage}
        </div>
      ) : optionsAreGrouped ? (
        renderGrouped()
      ) : (
        renderSimple()
      )}
    </Dropdown.Menu>
  );

  return (
    <Dropdown autoClose={multiple ? 'outside' : true} className="w-100">
      <Dropdown.Toggle
        as={Button}
        variant="primary"
        className={`${triggerClassName} fusion-theme-dropdown-toggle`}
        style={triggerStyle}
      >
        <span className="ms-2 text-truncate max-w-lg-3xs" style={{ maxWidth: 170, fontSize: 14 }}>
          {dropdownLabel()}
        </span>
        {multiple && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selected devices"
            className="text-white p-0 ms-auto me-2 d-inline-flex align-items-center text-decoration-none"
            onClick={handleClearAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClearAll(e);
            }}
          >
            <FaXmark />
          </span>
        )}
        <FaChevronDown className={multiple ? 'me-2' : 'ms-auto me-2'} />
      </Dropdown.Toggle>
      {menu}
    </Dropdown>
  );
}
